import { ProductModel, ProductPayload } from "../../components/models/product/product";
import axiosInstance, { apiError } from "../axiosIntance";

/** The API already normalises its payload — this guards against nulls and legacy rows. */
export const normalizeProduct = (raw: any): ProductModel => ({
    id: Number(raw?.id),
    name: raw?.name ?? '',
    description: raw?.description ?? '',
    price: Number(raw?.price) || 0,
    stock: Number(raw?.stock) || 0,
    category: raw?.category ?? '',
    imageUrl: Array.isArray(raw?.imageUrl)
        ? raw.imageUrl.filter(Boolean)
        : raw?.imageUrl
            ? [raw.imageUrl]
            : [],
    discount: Number(raw?.discount) || 0,
    discountedPrice: Number(raw?.discountedPrice ?? raw?.price) || 0,
});

/** `description` is bullet lines joined with "|". */
export const productBullets = (description?: string): string[] =>
    (description || '')
        .split('|')
        .map((line) => line.trim())
        .filter(Boolean);

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Mirrors the backend pricing rule: an explicit product discount wins,
 * otherwise an identified customer gets 10% off. The two never stack.
 */
export const unitPriceFor = (product: ProductModel | null, hasEmail: boolean): number => {
    if (!product) return 0;
    if (product.discount > 0) return round2(product.discountedPrice);
    return hasEmail ? round2(product.price * 0.9) : round2(product.price);
};

export const savingsPercent = (product: ProductModel | null, hasEmail: boolean): number => {
    if (!product || !product.price) return 0;
    const unit = unitPriceFor(product, hasEmail);
    return Math.round(((product.price - unit) / product.price) * 100);
};

/** Largest file the upload form accepts, in bytes. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ProductServices = {
    getProducts: async (): Promise<ProductModel[]> => {
        try {
            const res = await axiosInstance.get('/products');
            return Array.isArray(res.data) ? res.data.map(normalizeProduct) : [];
        } catch (err: any) {
            throw apiError(err, 'Failed to load products');
        }
    },

    getProductById: async (id: number): Promise<ProductModel> => {
        try {
            const res = await axiosInstance.get(`/products/${id}`);
            return normalizeProduct(res.data);
        } catch (err: any) {
            throw apiError(err, 'Failed to load product');
        }
    },

    // --- admin only, requires the bearer token ---------------------------
    /**
     * POST /api/upload — multipart, field name `image`, one file per call.
     * Returns the server path ("/uploads/rose-serum-9a3f2b1c.jpg") to store on
     * the product. The server slugifies the name and adds the unique suffix.
     */
    uploadImage: async (file: File): Promise<string> => {
        try {
            const form = new FormData();
            form.append('image', file);
            const res = await axiosInstance.post('/upload', form);
            return res.data?.path || '';
        } catch (err: any) {
            throw apiError(err, 'Failed to upload image');
        }
    },

    // Writes answer with { message, product }; reads answer with the row itself.
    createProduct: async (productData: ProductPayload): Promise<ProductModel> => {
        try {
            const res = await axiosInstance.post('/products', productData);
            return normalizeProduct(res.data?.product ?? res.data);
        } catch (err: any) {
            throw apiError(err, 'Failed to create product');
        }
    },

    updateProduct: async (id: number, productData: Partial<ProductPayload>): Promise<ProductModel> => {
        try {
            const res = await axiosInstance.patch(`/products/${id}`, productData);
            return normalizeProduct(res.data?.product ?? res.data);
        } catch (err: any) {
            throw apiError(err, 'Failed to update product');
        }
    },

    deleteProduct: async (id: number) => {
        try {
            const res = await axiosInstance.delete(`/products/${id}`);
            return res.data;
        } catch (err: any) {
            throw apiError(err, 'Failed to delete product');
        }
    },
};
